import { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import NavbarAdmin from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../components/Toast";
import { t } from "../i18n";
import { ReceiptDocument, ScaledPreview, downloadReceiptPdf, fmt } from "./ReceiptDocument";

const API = process.env.REACT_APP_API_URL || "";
const getToken = () => localStorage.getItem("token");
const authHeader = () => ({ Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" });

const inputStyle = {
  width: "100%", padding: "10px 14px", borderRadius: 8,
  border: "1px solid #d1d5db", fontSize: 14, background: "#fff",
  color: "#111827", boxSizing: "border-box", outline: "none", fontFamily: "inherit",
};
const labelStyle = { fontSize: 13, color: "#374151", display: "block", marginBottom: 6, fontWeight: 400 };
const cardStyle  = { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "20px 24px" };
const btnDark    = { padding: "10px 22px", borderRadius: 8, border: "none", background: "#1e3a5f", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500 };
const btnGhost   = { padding: "10px 18px", borderRadius: 8, border: "2px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13, color: "#374151", fontWeight: 500 };

const fmtThaiDate = iso =>
  iso ? new Date(iso).toLocaleDateString("th-TH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";

const PAGE_SIZE = 20;

export default function ReceiptArchive() {
  const { lang } = useLanguage();
  const printRef = useRef(null);

  /* filters */
  const [q,        setQ]        = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [status,   setStatus]   = useState("");

  /* list */
  const [data,    setData]    = useState({ rows: [], total: 0, page: 1, pages: 1 });
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  /* view modal */
  const [viewDoc,       setViewDoc]       = useState(null);   // doc เต็มจาก GET /api/receipts/:id
  const [viewLoading,   setViewLoading]   = useState(false);
  const [busy,          setBusy]          = useState(false);  // กำลังพิมพ์/PDF/void
  const [showVoidInput, setShowVoidInput] = useState(false);
  const [voidReason,    setVoidReason]    = useState("");

  /* toast */
  const [toast, setToast] = useState("");
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(""), 2400); };

  const buildParams = useCallback((p, limit) => {
    const params = new URLSearchParams();
    if (q.trim())  params.set("q", q.trim());
    if (dateFrom)  params.set("dateFrom", dateFrom);
    if (dateTo)    params.set("dateTo", dateTo);
    if (status)    params.set("status", status);
    params.set("page", String(p));
    params.set("limit", String(limit));
    return params;
  }, [q, dateFrom, dateTo, status]);

  const fetchList = useCallback(async (p = 1) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/receipts?${buildParams(p, PAGE_SIZE)}`, { headers: authHeader() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setData(d);
      setPage(d.page || p);
    } catch { setError(t(lang, "raLoadFail")); }
    setLoading(false);
  }, [buildParams, lang]);

  /* โหลดครั้งแรก */
  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchList(1);

  /* ── view modal ── */
  const openView = async (row) => {
    setViewLoading(true);
    setShowVoidInput(false);
    setVoidReason("");
    try {
      const res = await fetch(`${API}/api/receipts/${row._id}`, { headers: authHeader() });
      if (!res.ok) throw new Error();
      setViewDoc(await res.json());
    } catch { showToast(t(lang, "raLoadFail")); }
    setViewLoading(false);
  };

  const closeView = () => { setViewDoc(null); setShowVoidInput(false); setVoidReason(""); };

  const markReprint = (id) =>
    fetch(`${API}/api/receipts/${id}/reprint`, { method: "PATCH", headers: authHeader() })
      .then(() => fetchList(page)).catch(() => {});

  /* ดาวน์โหลด PDF — เลขที่เดิม */
  const handlePdf = async () => {
    if (!viewDoc || busy) return;
    setBusy(true);
    try {
      await downloadReceiptPdf(printRef.current, `ใบเสร็จ_${viewDoc.receiptNo}.pdf`);
      await markReprint(viewDoc._id);
    } catch { showToast(t(lang, "raLoadFail")); }
    finally { setBusy(false); }
  };

  /* ยกเลิกใบเสร็จ */
  const handleVoid = async () => {
    if (!viewDoc || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/receipts/${viewDoc._id}/void`, {
        method: "PATCH", headers: authHeader(),
        body: JSON.stringify({ reason: voidReason }),
      });
      if (!res.ok) throw new Error();
      setViewDoc(p => ({ ...p, voided: true, voidReason }));
      setShowVoidInput(false);
      fetchList(page);
    } catch { showToast(t(lang, "raVoidFail")); }
    finally { setBusy(false); }
  };

  /* ── Excel export (ตาม filter ปัจจุบัน ทั้งหมดทุกหน้า) ── */
  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/api/receipts?${buildParams(1, 0)}`, { headers: authHeader() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      const rows = d.rows || [];
      if (rows.length === 0) { showToast(t(lang, "raNoData")); return; }

      const sheetData = rows.map((r, i) => ({
        "#":                 i + 1,
        "เลขที่ใบเสร็จ":     r.receiptNo,
        "วันที่":            r.receiptDate,
        "ลูกค้า":            r.customerName || "-",
        "ยอดก่อนภาษี (บาท)": r.subtotal,
        "VAT 7% (บาท)":      r.vatAmount,
        "หัก ณ ที่จ่าย 3% (บาท)": r.whtAmount,
        "ยอดสุทธิ (บาท)":    r.grandTotal,
        "พิมพ์ซ้ำ (ครั้ง)":  r.reprintCount || 0,
        "สถานะ":             r.voided ? t(lang, "raStatusVoid") : t(lang, "raStatusOk"),
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      ws["!cols"] = [
        { wch: 5 }, { wch: 18 }, { wch: 12 }, { wch: 26 }, { wch: 16 },
        { wch: 13 }, { wch: 18 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "คลังใบเสร็จ");
      const range = dateFrom || dateTo ? `_${dateFrom || ""}_${dateTo || ""}` : "";
      XLSX.writeFile(wb, `คลังใบเสร็จ${range}.xlsx`);
    } catch { showToast(t(lang, "raLoadFail")); }
  };

  const rows = data.rows || [];

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <NavbarAdmin />
      <Toast show={!!toast} type="info" message={toast} lang={lang} />

      <style>{`
        @media (max-width: 820px) {
          .ra-filter-grid { grid-template-columns: 1fr !important; }
          .ra-main-pad { padding: 20px 16px !important; }
          .ra-table-wrap { overflow-x: auto; }
        }
      `}</style>

      <div className="ra-main-pad" style={{ padding: "28px 32px", maxWidth: 1200, margin: "0 auto" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 20 }}>🗂</span>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#111827" }}>{t(lang, "raTitle")}</h1>
        </div>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: "#6b7280" }}>{t(lang, "raSub")}</p>

        {/* filter card */}
        <div style={{ ...cardStyle, marginBottom: 18 }}>
          <div className="ra-filter-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto auto", gap: 12, alignItems: "end" }}>
            <div>
              <label style={labelStyle}>{t(lang, "raSearch")}</label>
              <input value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleSearch(); }}
                placeholder={t(lang, "raSearchPh")} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t(lang, "raDateFrom")}</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t(lang, "raDateTo")}</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t(lang, "raColStatus")}</label>
              <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
                <option value="">{t(lang, "raStatusAll")}</option>
                <option value="active">{t(lang, "raStatusOk")}</option>
                <option value="voided">{t(lang, "raStatusVoid")}</option>
              </select>
            </div>
            <button onClick={handleSearch} disabled={loading} style={{ ...btnDark, opacity: loading ? 0.6 : 1 }}>
              {loading ? t(lang, "raLoading") : t(lang, "raLoad")}
            </button>
            <button onClick={handleExport} style={btnGhost}>{t(lang, "raDownloadExcel")}</button>
          </div>
        </div>

        {/* results */}
        <div style={cardStyle}>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
            {t(lang, "raTotal")} <b style={{ color: "#111827" }}>{data.total || 0}</b> {t(lang, "raUnit")}
          </p>

          {error && <p style={{ color: "#dc2626", fontSize: 13, textAlign: "center", padding: "18px 0" }}>{error}</p>}
          {!error && loading && <p style={{ color: "#9ca3af", fontSize: 13, textAlign: "center", padding: "18px 0" }}>{t(lang, "raLoading")}</p>}
          {!error && !loading && rows.length === 0 && (
            <p style={{ color: "#9ca3af", fontSize: 13, textAlign: "center", padding: "18px 0" }}>{t(lang, "raNoData")}</p>
          )}

          {!error && !loading && rows.length > 0 && (
            <div className="ra-table-wrap">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left", color: "#6b7280" }}>
                    <th style={{ padding: "8px 10px", fontWeight: 500 }}>#</th>
                    <th style={{ padding: "8px 10px", fontWeight: 500 }}>{t(lang, "raColReceiptNo")}</th>
                    <th style={{ padding: "8px 10px", fontWeight: 500 }}>{t(lang, "raColDate")}</th>
                    <th style={{ padding: "8px 10px", fontWeight: 500 }}>{t(lang, "raColCustomer")}</th>
                    <th style={{ padding: "8px 10px", fontWeight: 500, textAlign: "right" }}>{t(lang, "raColTotal")}</th>
                    <th style={{ padding: "8px 10px", fontWeight: 500 }}>{t(lang, "raColStatus")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r._id} onClick={() => openView(r)}
                      style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "10px", color: "#9ca3af" }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td style={{ padding: "10px", fontWeight: 600, color: "#1e3a5f" }}>{r.receiptNo}</td>
                      <td style={{ padding: "10px" }}>{fmtThaiDate(r.receiptDate)}</td>
                      <td style={{ padding: "10px" }}>{r.customerName || "-"}</td>
                      <td style={{ padding: "10px", textAlign: "right", fontWeight: 500 }}>{fmt(r.grandTotal)}</td>
                      <td style={{ padding: "10px" }}>
                        <span style={{
                          display: "inline-block", padding: "3px 10px", borderRadius: 999,
                          fontSize: 12, fontWeight: 500,
                          background: r.voided ? "#fee2e2" : "#dcfce7",
                          color:      r.voided ? "#b91c1c" : "#15803d",
                        }}>
                          {r.voided ? t(lang, "raStatusVoid") : t(lang, "raStatusOk")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* pagination */}
          {!error && !loading && (data.pages || 1) > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 14, marginTop: 16 }}>
              <button onClick={() => fetchList(page - 1)} disabled={page <= 1}
                style={{ ...btnGhost, padding: "7px 14px", opacity: page <= 1 ? 0.4 : 1 }}>{t(lang, "raPrev")}</button>
              <span style={{ fontSize: 13, color: "#374151" }}>{t(lang, "raPage")} {page} / {data.pages}</span>
              <button onClick={() => fetchList(page + 1)} disabled={page >= data.pages}
                style={{ ...btnGhost, padding: "7px 14px", opacity: page >= data.pages ? 0.4 : 1 }}>{t(lang, "raNext")}</button>
            </div>
          )}
        </div>
      </div>

      {/* ── View modal ── */}
      {(viewLoading || viewDoc) && (
        <div onClick={e => { if (e.target === e.currentTarget) closeView(); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100,
                   display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 640,
                        maxHeight: "92vh", display: "flex", flexDirection: "column",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.18)", overflow: "hidden" }}>
            {viewLoading || !viewDoc ? (
              <p style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: "48px 0" }}>{t(lang, "raLoading")}</p>
            ) : (
              <>
                {/* header */}
                <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #e5e7eb",
                              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: "#111827" }}>{viewDoc.receiptNo}</p>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
                      {fmtThaiDate(viewDoc.receiptDate)} · {viewDoc.customerName || "-"} · {fmt(viewDoc.grandTotal)} THB
                    </p>
                  </div>
                  <button onClick={closeView}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22, lineHeight: 1 }}>✕</button>
                </div>

                {/* voided banner */}
                {viewDoc.voided && (
                  <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 24px", fontSize: 13, fontWeight: 500 }}>
                    ⚠ {t(lang, "raVoidedBanner")}{viewDoc.voidReason ? ` — ${viewDoc.voidReason}` : ""}
                  </div>
                )}

                {/* preview */}
                <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px", display: "flex", justifyContent: "center", background: "#f9fafb" }}>
                  <ScaledPreview data={viewDoc.snapshot} />
                </div>

                {/* actions */}
                <div style={{ borderTop: "1px solid #e5e7eb", padding: "14px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {showVoidInput ? (
                    <div style={{ display: "flex", gap: 8 }}>
                      <input autoFocus value={voidReason} onChange={e => setVoidReason(e.target.value)}
                        placeholder={t(lang, "raVoidReasonPh")} style={{ ...inputStyle, flex: 1 }} />
                      <button onClick={handleVoid} disabled={busy}
                        style={{ padding: "10px 16px", borderRadius: 8, border: "none", background: "#dc2626", color: "#fff",
                                 cursor: "pointer", fontSize: 13, fontWeight: 500, opacity: busy ? 0.6 : 1 }}>
                        {t(lang, "raVoidConfirm")}
                      </button>
                      <button onClick={() => { setShowVoidInput(false); setVoidReason(""); }} style={btnGhost}>
                        {t(lang, "raVoidCancel")}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button onClick={handlePdf} disabled={busy} style={{ ...btnGhost, flex: 1, opacity: busy ? 0.6 : 1 }}>
                        {t(lang, "reDownloadPdf")}
                      </button>
                      {!viewDoc.voided && (
                        <button onClick={() => setShowVoidInput(true)} disabled={busy}
                          style={{ ...btnGhost, color: "#dc2626", borderColor: "#fca5a5", opacity: busy ? 0.6 : 1 }}>
                          {t(lang, "raVoid")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Hidden print/PDF target — ต้นฉบับ + สำเนา (เลขที่เดิมตาม snapshot) */}
      {viewDoc && (
        <div style={{ position: "absolute", left: -9999, top: -9999, pointerEvents: "none" }}>
          <div ref={printRef}>
            <ReceiptDocument data={viewDoc.snapshot} variant="original" />
            <div style={{ pageBreakBefore: "always" }} />
            <ReceiptDocument data={viewDoc.snapshot} variant="copy" />
          </div>
        </div>
      )}
    </div>
  );
}
